// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek
import { Action, createAction, createReducer } from "@reduxjs/toolkit";
import cloneDeep from "lodash.clonedeep";

import localeData from "locale/all";

import { INITIAL_STATE, IState } from "states/states";
import { ActionType, IAction, IActionTypeParameterLoad } from "actions";
import { ICloudParameter, ILinxParameter } from "parameter";
import { ModalPanel } from "states/guistates";
import guiReducer from "./guireducer";
import siteReducer from "./sitereducer";
import dataReducer from "./datareducer";
import schedulerReducer from "./schedulerreducer";
import trendingReducer from "./trendingreducer";
import ocppReducer from "./ocppreducer";
import controllerReducer from "./controllerreducer";
import cacheReducer from "./cachereducer";
import { IStateRouter } from "states/routerstates";

// uses webpack definitions
declare const GIT_VERSION: string;

function prepareCachedStartup(state: IState) {
  // this is a first startup based on local storage browser cache
  const isFullCache = state?.gitVersion === GIT_VERSION;
  console.log("browser cache startup:", isFullCache ? "full" : "partial");
  // set undefined state members
  Object.keys(INITIAL_STATE).forEach((curItem) => {
    const curMember = curItem as any as keyof IState;
    // reset state if
    // - data not present in cache or
    // - only partial startup and member is not "cache"
    if (
      (INITIAL_STATE.hasOwnProperty(curMember) && !state.hasOwnProperty(curMember)) ||
      (!isFullCache && curMember !== "cached")
    ) {
      // eslint-disable-next-line
      (state[curMember] as any) = cloneDeep(INITIAL_STATE[curMember]);
    }
  });
  if (isFullCache) {
    // manual reset of some stored members
    state.gui.modal.panel = ModalPanel.NONE;
    state.gui.confirmModal.isOpen = false;
    state.gui.modalInput = undefined;
    // reset graph to inhibit loading long month data
    state.gui.trendPeriod = cloneDeep(INITIAL_STATE.gui.trendPeriod);
    state.gui.ioPeriod = cloneDeep(INITIAL_STATE.gui.ioPeriod);
  }
}

export function prepareInitialState(state: IState | undefined): IState {
  // initial state loading
  // check whether we have data in local browser storage
  // state loading needed if not present, from other SW, or just loaded from cache without needed data
  if (
    !state ||
    (state.cached && state.cached.version !== INITIAL_STATE.cached.version) ||
    new URL(window.document.URL).searchParams.has("clean")
  ) {
    // very first startup with empty browser cache
    // create initial state
    console.log("preparing initial startup");
    state = INITIAL_STATE; // we do not need to clone this as the state is anyway immutable
  } else {
    prepareCachedStartup(state);
  }
  return state;
}

function getLocale() {
  // define user's language. Different browsers have the user locale defined on different fields
  const language = (navigator.languages && navigator.languages[0]) || navigator.language || "de";
  // split locales with a region code
  return language.toLowerCase().split(/[_-]+/)[0];
}

function parameterReducer(
  state: ICloudParameter | ILinxParameter | undefined,
  action: IAction<any>,
) {
  switch (action.type) {
    case ActionType.ACT_GET_PARAMETER_SUCC: {
      const payload = action.payload as IActionTypeParameterLoad;
      return payload.parameter;
    }

    case ActionType.ACT_DISCONNECT_SITE:
      state = undefined;
      return state;

    default:
      return state;
  }
}

export const reducer = createReducer(INITIAL_STATE, (builder) => {
  builder
    .addCase(createAction(ActionType.ACT_APP_LOADED), (state, action) => {
      state.gitVersion = GIT_VERSION;
      // set locale by just overriding the intial state message constant
      state.intl.locale = getLocale();
      state.intl.messages = localeData[state.intl.locale] ?? localeData.en;
      //console.log("initial state", current(state));
    })
    .addCase(createAction<IStateRouter>(ActionType.ACT_NAVIGATION), (state, action) => {
      state.router = action.payload;
    })
    // combineReducers
    .addMatcher<IAction<any>>(
      () => true,
      (state, action) => {
        state.cached = cacheReducer(state.cached, action, state);
      },
    )
    .addMatcher<IAction<any>>(
      () => true,
      (state, action) => {
        state.gui = guiReducer(state.gui, action, state);
      },
    )
    .addMatcher<IAction<any>>(
      () => true,
      (state, action) => {
        state.scheduler = schedulerReducer(state.scheduler, action, state);
      },
    )
    .addMatcher<IAction<any>>(
      () => true,
      (state, action) => {
        state.site = siteReducer(state.site, action, state);
      },
    )
    .addMatcher<IAction<any>>(
      () => true,
      (state, action) => {
        state.parameter = parameterReducer(state.parameter, action);
      },
    )
    .addMatcher<IAction<any>>(
      () => true,
      (state, action) => {
        state.data = dataReducer(state.data, action, state);
      },
    )
    .addMatcher<IAction<any>>(
      () => true,
      (state, action) => {
        state.trending = trendingReducer(state.trending, action, state);
      },
    )
    .addMatcher<IAction<any>>(
      () => true,
      (state, action) => {
        state.ocpp = ocppReducer(state.ocpp, action, state);
      },
    )
    .addMatcher<IAction<any>>(
      () => true,
      (state, action) => {
        state.controller = controllerReducer(state.controller, action);
      },
    );
});
